import type React from "react";
import { useState, useEffect, useRef } from "react";
import { Table, TableBody } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateMetadata, selectPageMetadata } from "../../store/features/appStateSlice";
import type { PaginationResponse, PaginationParam } from "../../types/paginationResponse";
import TablePagination from "./TablePagination";
import { TableHead, TableRow, TableCell, Box } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import TableSearch from "./TableSearch";
import StyledTable from "./StyledTable";

type TranslationKeys = string;

export type TableFilterValue = string | number | boolean | null;

export interface TableFilters {
  [key: string]: TableFilterValue;
  sortBy: string;
  sortOrder: "asc" | "desc";
}

export interface TableFilterConfig {
  id: string;
  label: string;
  type: "text" | "select" | "number" | "date" | "boolean";
  options?: Array<{ value: string; label: string }>;
  defaultValue?: TableFilterValue;
  formatter?: (value: string) => string;
}

export interface TableSearchProps {
  searchFilters: TableFilters;
  onFilterChange: (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearField: (field: string) => void;
  onClearAll: () => void;
  onSearch: () => void;
  onKeyPress: (event: React.KeyboardEvent) => void;
  filters: TableFilterConfig[];
  onDownload?: (filters: TableFilters) => void;
}

interface Column<T> {
  id: string;
  label: string;
  sortable?: boolean;
  align?: "left" | "center" | "right";
}

interface TableHeaderProps<T> {
  columns: Column<T>[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort?: (field: string) => void;
}

const TableHeader = <T,>({ columns, sortBy, sortOrder, onSort }: TableHeaderProps<T>) => {
  const { t } = useTranslation();

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  };

  return (
    <TableHead>
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.id}
            sx={{
              fontWeight: "bold",
              textAlign: column.align || "left",
              ...(column.sortable && {
                cursor: "pointer",
                "&:hover": { backgroundColor: "action.hover" },
              }),
            }}
            onClick={() => column.sortable && onSort?.(column.id)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {t(column.label as TranslationKeys)}
              {column.sortable && getSortIcon(column.id)}
            </Box>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

type SortByField = "type" | "cityId" | "status" | "timestamp";

type FilterValue = string | number | boolean | null;

interface SearchFilters {
  [key: string]: FilterValue;
  sortBy: string;
  sortOrder: "asc" | "desc";
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  onSearch?: (filters: TableFilters, pagination: PaginationParam) => Promise<void>;
  showSearch?: boolean;
  pagination?: PaginationResponse | null;
  onPaginationChange?: (pagination: PaginationParam) => void;
  renderRow: (
    item: T,
    props: {
      isExpanded: boolean;
      onToggle: (id: string) => void;
    },
  ) => React.ReactNode;
  searchFilters?: TableFilterConfig[];
  loading?: boolean;
  showRowsPerPage?: boolean;
  testId?: string;
  onDownload?: (filters: TableFilters) => void;
}

const DataTable = <T extends { _id: string }>({
  data,
  columns,
  onSearch,
  showSearch = true,
  pagination,
  onPaginationChange,
  renderRow,
  searchFilters = [],
  loading = false,
  showRowsPerPage = false,
  testId,
  onDownload,
}: TableProps<T>) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentMetadata = useSelector(selectPageMetadata);
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const isInitialMount = useRef(true);
  const prevExpandedRowId = useRef<string | null>(null);
  const [filters, setFilters] = useState<TableFilters>(() => {
    const initialFilters: TableFilters = {
      sortBy: searchParams.get("sortBy") || "",
      sortOrder: (searchParams.get("sortOrder") as "asc" | "desc") || "asc",
    };
    // Initialize filter values from URL params
    for (const filter of searchFilters) {
      initialFilters[filter.id] = searchParams.get(filter.id) || "";
    }
    return initialFilters;
  });

  // Sync filters with URL params when they change
  useEffect(() => {
    const newFilters: TableFilters = {
      sortBy: searchParams.get("sortBy") || "",
      sortOrder: (searchParams.get("sortOrder") as "asc" | "desc") || "asc",
    };
    // Sync filter values from URL params, but preserve existing local values if URL param is empty
    for (const filter of searchFilters) {
      const urlValue = searchParams.get(filter.id);
      // Only update from URL if URL has a value, otherwise preserve existing local value
      if (urlValue !== null && urlValue !== "") {
        newFilters[filter.id] = urlValue;
      }
    }
    // Use functional update to avoid dependency on filters
    setFilters((prevFilters) => {
      // Merge URL values with existing local values (preserve local values if URL doesn't have them)
      const mergedFilters: TableFilters = {
        ...prevFilters,
        sortBy: newFilters.sortBy || prevFilters.sortBy || "",
        sortOrder: newFilters.sortOrder || prevFilters.sortOrder || "asc",
      };

      // Only update filter values from URL if URL has a value, otherwise keep existing local value
      for (const filter of searchFilters) {
        const urlValue = searchParams.get(filter.id);
        if (urlValue !== null && urlValue !== "") {
          mergedFilters[filter.id] = urlValue;
        } else if (prevFilters[filter.id] !== undefined) {
          // Preserve existing local value if URL doesn't have it
          mergedFilters[filter.id] = prevFilters[filter.id];
        } else {
          mergedFilters[filter.id] = "";
        }
      }

      // Check if anything actually changed
      let hasChanges = false;
      if (
        prevFilters.sortBy !== mergedFilters.sortBy ||
        prevFilters.sortOrder !== mergedFilters.sortOrder
      ) {
        hasChanges = true;
      }
      for (const filter of searchFilters) {
        if (prevFilters[filter.id] !== mergedFilters[filter.id]) {
          hasChanges = true;
          break;
        }
      }
      return hasChanges ? mergedFilters : prevFilters;
    });
  }, [searchParams, searchFilters]);

  // Initialize expanded row from URL
  useEffect(() => {
    const expandedRowId = searchParams.get("expandedRow");

    // Only update if the expanded row ID has changed
    if (expandedRowId !== prevExpandedRowId.current) {
      if (expandedRowId) {
        setExpandedRows(new Set([expandedRowId]));
        dispatch(updateMetadata({ ...currentMetadata, tableExpandedRowId: expandedRowId }));
      } else if (prevExpandedRowId.current) {
        // Clear expanded row if it was previously expanded
        setExpandedRows(new Set());
        const { tableExpandedRowId, ...restMetadata } = currentMetadata;
        dispatch(updateMetadata(restMetadata));
      }
      prevExpandedRowId.current = expandedRowId;
    }

    isInitialMount.current = false;
  }, [searchParams, dispatch, currentMetadata]);

  // Validate that onSearch is defined when showSearch is true
  if (showSearch && !onSearch) {
    throw new Error("onSearch prop is required when showSearch is true");
  }

  const handleSearch = async () => {
    if (!onSearch) return;
    await onSearch(filters, { page: 1, pageSize: 10 });
  };

  const handleFilterChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let value: TableFilterValue = event.target.value;
    // Convert value based on field type
    const filter = searchFilters.find((f) => f.id === field);
    if (filter) {
      switch (filter.type) {
        case "number":
          value = event.target.value === "" ? null : Number(event.target.value);
          break;
        case "boolean":
          value = event.target.checked;
          break;
        case "date":
          value = event.target.value || null;
          break;
        default:
          value = event.target.value;
      }
    }

    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearField = (field: string) => {
    const newFilters = {
      ...filters,
      [field]: "",
    };
    setFilters(newFilters);

    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(field);
    setSearchParams(newParams);

    if (onSearch) {
      onSearch(newFilters, { page: 1, pageSize: 10 });
    }
  };

  const handleClearAll = () => {
    // Create a new filters object with all fields explicitly set to empty
    const newFilters: TableFilters = {
      sortBy: "",
      sortOrder: "asc",
    };

    // Reset all search filter fields to empty
    for (const filter of searchFilters) {
      // Always set to empty string for select fields
      newFilters[filter.id] = "";
    }

    // Update the filters state
    setFilters(newFilters);

    // Clear URL search parameters
    setSearchParams({});

    // Trigger search with cleared filters
    if (onSearch) {
      onSearch(newFilters, { page: 1, pageSize: 10 });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set<string>();
      if (prev.has(id)) {
        newSet.delete(id);
        const { tableExpandedRowId, ...restMetadata } = currentMetadata;
        dispatch(updateMetadata(restMetadata));
        searchParams.delete("expandedRow");
      } else {
        newSet.add(id);
        dispatch(updateMetadata({ ...currentMetadata, tableExpandedRowId: id }));
        searchParams.set("expandedRow", id);
      }
      setSearchParams(searchParams);
      return newSet;
    });
  };

  return (
    <>
      {showSearch && (
        <TableSearch
          searchFilters={filters}
          onFilterChange={handleFilterChange}
          onClearField={handleClearField}
          onClearAll={handleClearAll}
          onSearch={handleSearch}
          onKeyPress={handleKeyPress}
          filters={searchFilters}
          onDownload={onDownload ? (f) => onDownload(f) : undefined}
        />
      )}
      <StyledTable loading={loading} testId={testId}>
        <Table>
          <TableHeader
            columns={columns}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            onSort={(field: string) => {
              if (!onSearch) return;
              const newOrder =
                filters.sortBy === field && filters.sortOrder === "asc" ? "desc" : "asc";
              const newFilters: TableFilters = {
                ...filters,
                sortBy: field,
                sortOrder: newOrder,
              };
              setFilters(newFilters);
              onSearch(newFilters, { page: 1, pageSize: 10 });
            }}
          />
          <TableBody>
            {data.map((item) =>
              renderRow(item, {
                isExpanded: expandedRows.has(item._id),
                onToggle: toggleRow,
              }),
            )}
          </TableBody>
        </Table>
      </StyledTable>
      {pagination && onPaginationChange && (
        <TablePagination
          count={pagination.total}
          pagination={{ page: pagination.page, pageSize: pagination.pageSize }}
          onPaginationChange={onPaginationChange}
          showRowsPerPage={showRowsPerPage}
        />
      )}
    </>
  );
};

export default DataTable;
